import { AccountSettings } from "@/components/pages/AccountSettings";
import { CalendarPage } from "@/components/pages/CalendarPage";
import { CandidatesPage } from "@/components/pages/CandidatesPage";
import { MatrixManager } from "@/components/pages/MatrixManager";
import { MyOrganizationPage } from "@/components/pages/MyOrganizationPage";
import { OrgAnalyticsPage } from "@/components/pages/OrgAnalyticsPage";
import { OrganizationsPage } from "@/components/pages/OrganizationsPage";
import { PermissionsPage } from "@/components/pages/PermissionsPage";
import { PipelinePage } from "@/components/pages/PipelinePage";
import { PositionManager } from "@/components/pages/PositionManager";
import { RolesPage } from "@/components/pages/RolesPage";
import { UserManager } from "@/components/pages/UserManager";
import { ScreenPage } from "@/components/pages/screen/ScreenPage";
import type { ScreeningJob } from "@/components/pages/screen/types";
import { CandidateProfile } from "@/components/shared/candidate/CandidateProfile";
import type { Tab as ProfileTab } from "@/components/shared/candidate/CandidateProfile";
import type { AuthUser, HiringMatrix, Position } from "@/types";

interface AppBodyProps {
  loading: boolean;
  effectivePage: string;
  authUser: AuthUser;
  positions: Position[];
  matrices: HiringMatrix[];
  selectedPositionId: number | null;
  selectedMatrixId: number | null;
  selectedMatrix: HiringMatrix | null;
  jobs: ScreeningJob[];
  usePipeline: boolean;
  profileCandidateId: number | null;
  profileTab: ProfileTab;
  profileDialog: "cv" | "interview" | "endorse" | null;
  orgAnalyticsId: number | null;
  onPositionChange: (id: number | null) => void;
  onMatrixChange: (id: number | null) => void;
  onFiles: (files: File[]) => void;
  onClearJobs: () => void;
  onGoToMatrices: () => void;
  pendingFile: File | null;
  onClearPendingFile: () => void;
  onTogglePipeline: (v: boolean) => void;
  onForceScreen: (job: ScreeningJob) => void;
  onViewCandidate: (id: number) => void;
  onPositionsChange: () => void;
  onMatricesChange: () => void;
  onViewProfile: (id: number) => void;
  onProfileTabChange: (tab: ProfileTab) => void;
  onProfileBack: () => void;
  onProfileDialogClose: () => void;
  onProfileOpenDialog: (dialog: "cv" | "interview" | "endorse") => void;
  onOrgAnalyticsBack: () => void;
  onProfileUpdate: (updates: Partial<AuthUser>) => void;
}

export function AppBody({
  loading,
  effectivePage,
  authUser,
  positions,
  matrices,
  selectedPositionId,
  selectedMatrixId,
  selectedMatrix,
  jobs,
  usePipeline,
  profileCandidateId,
  profileTab,
  profileDialog,
  orgAnalyticsId,
  onPositionChange,
  onMatrixChange,
  onFiles,
  onClearJobs,
  onGoToMatrices,
  pendingFile,
  onClearPendingFile,
  onTogglePipeline,
  onForceScreen,
  onViewCandidate,
  onPositionsChange,
  onMatricesChange,
  onViewProfile,
  onProfileTabChange,
  onProfileBack,
  onProfileDialogClose,
  onProfileOpenDialog,
  onOrgAnalyticsBack,
  onProfileUpdate,
}: AppBodyProps) {
  if (loading) {
    return (
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex flex-1 items-center justify-center">
          <p className="text-text-secondary">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {effectivePage === "screen" ? (
        <ScreenPage
          positions={positions}
          matrices={matrices}
          selectedPositionId={selectedPositionId}
          selectedMatrixId={selectedMatrixId}
          selectedMatrix={selectedMatrix}
          jobs={jobs}
          onPositionChange={onPositionChange}
          onMatrixChange={onMatrixChange}
          onFiles={onFiles}
          onClearJobs={onClearJobs}
          noMatrices={matrices.length === 0}
          pendingFile={pendingFile}
          onClearPendingFile={onClearPendingFile}
          onGoToMatrices={onGoToMatrices}
          usePipeline={usePipeline}
          onTogglePipeline={onTogglePipeline}
          onForceScreen={onForceScreen}
          onViewCandidate={onViewCandidate}
        />
      ) : effectivePage === "calendar" ? (
        <CalendarPage authUser={authUser} />
      ) : effectivePage === "pipeline" ? (
        <PipelinePage positions={positions} currentUser={authUser} onViewProfile={onViewProfile} />
      ) : effectivePage === "positions" ? (
        <PositionManager positions={positions} currentUser={authUser} onChange={onPositionsChange} />
      ) : effectivePage === "matrices" ? (
        <MatrixManager positions={positions} currentUser={authUser} onPositionsChange={onMatricesChange} />
      ) : effectivePage === "candidates" ? (
        <CandidatesPage positions={positions} currentUser={authUser} onViewProfile={onViewProfile} />
      ) : effectivePage === "archived" ? (
        <CandidatesPage positions={positions} archivedMode currentUser={authUser} onViewProfile={onViewProfile} />
      ) : effectivePage === "candidate-profile" && profileCandidateId ? (
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-8">
            <CandidateProfile
              candidateId={profileCandidateId}
              activeTab={profileTab}
              onTabChange={onProfileTabChange}
              onBack={onProfileBack}
              currentUser={authUser}
              initialDialog={profileDialog ?? undefined}
              onDialogClose={onProfileDialogClose}
              onOpenDialog={onProfileOpenDialog}
            />
          </div>
        </div>
      ) : effectivePage === "users" ? (
        <UserManager currentUser={authUser} />
      ) : effectivePage === "org-analytics" && orgAnalyticsId ? (
        <OrgAnalyticsPage orgId={orgAnalyticsId} onBack={onOrgAnalyticsBack} />
      ) : effectivePage === "organizations" ? (
        <OrganizationsPage />
      ) : effectivePage === "roles" ? (
        authUser.role === "admin" && authUser.org_id
          ? <MyOrganizationPage orgId={authUser.org_id} orgName={authUser.org_name ?? "Roles"} />
          : <RolesPage />
      ) : effectivePage === "permissions" ? (
        <PermissionsPage />
      ) : effectivePage === "settings" ? (
        <AccountSettings authUser={authUser} onProfileUpdate={onProfileUpdate} />
      ) : (
        <CandidatesPage positions={positions} currentUser={authUser} />
      )}
    </div>
  );
}
